import React, { memo, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { toast } from "react-toastify";
import { ADMISSION_COLORS as C } from "../../constants/admissionTheme";
import {
  getDocumentUploadErrorMessage,
  processDocumentUpload,
} from "../../utils/processDocumentUpload";

function ScanDocumentDialog({ open, onClose, onAutofill, onTakePhoto }) {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setPreviewUrl(null);
    setLoading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setPreviewUrl(null);
    setLoading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [open]);

  const handleClose = () => {
    onClose();
    reset();
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const handleExtract = async () => {
    if (!file) {
      toast.error("Please select a document to upload.");
      return;
    }

    setLoading(true);
    try {
      const result = await processDocumentUpload(file);
      if (!result.ok) {
        toast.error(result.error);
        return;
      }

      onAutofill(result.documentJson);
      toast.success("Document scanned and form autofilled successfully!");
      handleClose();
    } catch (err) {
      console.error("[Document Scan] error:", err?.response?.status, err?.response?.data, err);
      toast.error(getDocumentUploadErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, color: C.primary }}>
        <QrCodeScannerIcon />
        Scan Document to Autofill
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color={C.textSecondary} mb={2}>
          Take a photo or upload an insurance card, ID card, or any patient document.
          The form will be autofilled with the extracted information.
        </Typography>

        <Box
          onClick={() => fileInputRef.current?.click()}
          sx={{
            border: `2px dashed ${C.primary}`,
            borderRadius: 2,
            p: 3,
            textAlign: "center",
            cursor: "pointer",
            bgcolor: C.info.bg,
            mb: 1.5,
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,application/pdf"
            hidden
            onChange={onFileChange}
          />
          {previewUrl ? (
            file?.type?.startsWith("image/") ? (
              <Box
                component="img"
                src={previewUrl}
                alt="Document preview"
                sx={{ maxWidth: "100%", maxHeight: 220, borderRadius: 1, objectFit: "contain" }}
              />
            ) : (
              <Box color={C.primary}>
                <UploadFileIcon sx={{ fontSize: 48 }} />
                <Typography mt={1}>{file?.name}</Typography>
              </Box>
            )
          ) : (
            <Box color={C.primary}>
              <UploadFileIcon sx={{ fontSize: 48 }} />
              <Typography fontWeight={600} mt={1}>Click to upload document</Typography>
              <Typography variant="caption" color={C.textMuted}>
                Supports: JPG, PNG, PDF
              </Typography>
            </Box>
          )}
        </Box>

        <Button
          variant="outlined"
          fullWidth
          onClick={onTakePhoto}
          sx={{ borderColor: C.primary, color: C.primary }}
        >
          Take Photo with Camera
        </Button>

        {file && (
          <Typography variant="body2" color={C.primary} textAlign="center" mt={1.5}>
            Selected: <strong>{file.name}</strong>
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button variant="contained" onClick={handleExtract} disabled={!file || loading}>
          {loading ? "Processing..." : "Extract & Autofill"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default memo(ScanDocumentDialog);
